import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../hooks/useSidebar';
import { FileText, Save, Search } from 'lucide-react';

import WorksheetNavigation from './components/WorksheetNavigation';
import HAZIDWorksheet from './components/HAZIDWorksheet';
import WhatIfChecklist from './components/WhatIfChecklist';
import HazardLibrary from './components/HazardLibrary';
import EquipmentTagging from './components/EquipmentTagging';
import PermitLinking from './components/PermitLinking';
import AIAssistantPanel from './components/AIAssistantPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/AppButton';

const HAZIDWorksheetManagerContent = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [activeTab, setActiveTab] = useState('worksheet');
  const [selectedWorksheet, setSelectedWorksheet] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Mock worksheets data
  const worksheets = [
    {
      id: 'HAZID-2025-001',
      title: 'Heat Exchanger Maintenance - HE-101',
      equipment: 'HE-101',
      status: 'in-progress',
      completionRate: 65,
      lastModified: '2025-12-04T10:30:00Z',
      identifiedHazards: 12,
      assignedTo: 'John Smith'
    },
    {
      id: 'HAZID-2025-002',
      title: 'Pump Installation - P-205',
      equipment: 'P-205',
      status: 'completed',
      completionRate: 100,
      lastModified: '2025-12-03T14:20:00Z',
      identifiedHazards: 8,
      assignedTo: 'Sarah Johnson'
    },
    {
      id: 'HAZID-2025-003',
      title: 'Storage Tank Inspection - TK-304',
      equipment: 'TK-304',
      status: 'draft',
      completionRate: 25,
      lastModified: '2025-12-04T09:15:00Z',
      identifiedHazards: 4,
      assignedTo: 'Mike Chen'
    }
  ];

  const tabs = [
    { id: 'worksheet', label: 'HAZID Worksheet', icon: FileText },
    { id: 'what-if', label: 'What-If Checklist', icon: FileText },
    { id: 'library', label: 'Hazard Library', icon: FileText },
    { id: 'equipment', label: 'Equipment Tagging', icon: FileText },
    { id: 'permits', label: 'Work Permit Linking', icon: FileText }
  ];

  const handleCreateNew = () => {
    // Create new worksheet logic
    console.log('Creating new HAZID worksheet');
  };

  const handleSave = () => {
    // Save worksheet logic
    console.log('Saving worksheet');
  };

  const handleExport = () => {
    // Export worksheet logic
    console.log('Exporting worksheet');
  };

  return (
    <div className={` bg-background min-h-screen`}>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">HAZID Worksheet Manager</h1>
              <p className="text-muted-foreground mt-1">
                Comprehensive hazard identification and risk assessment workspace
              </p>
            </div>
            <Button
              variant="outline"
              iconName="Home"
              onClick={() => navigate('/')}
            >
              Return to Dashboard
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Worksheet Navigation */}
          <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search worksheets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <WorksheetNavigation
              worksheets={worksheets}
              selectedWorksheet={selectedWorksheet}
              onSelectWorksheet={setSelectedWorksheet}
              searchQuery={searchQuery}
            />
          </div>

          {/* Center Panel - Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Tabs */}
            <div className="bg-white border-b border-gray-200 px-6">
              <div className="flex gap-1">
                {tabs?.map((tab) => {
                  const Icon = tab?.icon;
                  return (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                        activeTab === tab?.id
                          ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="font-medium">{tab?.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'worksheet' && <HAZIDWorksheet worksheet={selectedWorksheet} />}
              {activeTab === 'what-if' && <WhatIfChecklist worksheet={selectedWorksheet} />}
              {activeTab === 'library' && <HazardLibrary />}
              {activeTab === 'equipment' && <EquipmentTagging worksheet={selectedWorksheet} />}
              {activeTab === 'permits' && <PermitLinking worksheet={selectedWorksheet} />}
            </div>
          </div>

          {/* Right Panel - AI Assistant */}
          <AIAssistantPanel worksheet={selectedWorksheet} activeTab={activeTab} />
        </div>
      </div>
    </div>
  );
};

export default HAZIDWorksheetManagerContent;